import type { ReactNode } from "react";
import React, { useRef } from "react";
import { getFocusStyle } from "../../../theme";
import { styled, type StyleObject } from "../../../theme/styled";
import { useDismiss } from "../../../util/useDismiss/useDismiss";
import { useMountTransition } from "../../../util/useMountTransition/useMountTransition";
import { BackDrop } from "../../util/BackDrop";
import { StyledPopoverBase } from "./Base";

export interface DrawerProps {
  isOpen: boolean;
  onClose: (focusTextField?: boolean) => void;
  children: ReactNode;
  dataTestId?: string;
  ariaLabel?: string;
  tabIndex?: number;
  role?: "dialog" | "menu";
  /** If you handle dissmiss yourself. e.g. the dialog uses this drawer but handles dismisses */
  disableUseDismiss?: boolean;
  /** Defaults to `"auto"`. */
  size?: "auto" | "full";
  backgroundColor?: StyleObject["backgroundColor"];
}

export const Drawer = ({
  children,
  ariaLabel,
  dataTestId,
  isOpen,
  onClose,
  tabIndex,
  role = "dialog",
  disableUseDismiss = false,
  size = "auto",
  backgroundColor,
}: DrawerProps): React.JSX.Element | null => {
  const drawerRef = useRef(null);
  useDismiss({
    handler: onClose,
    enabled: isOpen && !disableUseDismiss,
    reference: drawerRef,
    escapeKey: true,
  });
  const hasTransitionedIn = useMountTransition(isOpen, 300);

  if (!isOpen && !hasTransitionedIn) return null;

  return (
    <>
      <BackDrop visible={isOpen} hasTransitionedIn={hasTransitionedIn} />
      <StyledDrawer
        id="popover"
        role={role}
        aria-modal={role === "dialog" ? true : undefined}
        aria-label={ariaLabel}
        ref={drawerRef}
        data-test-id={dataTestId}
        isOpen={isOpen}
        hasTransitionedIn={hasTransitionedIn}
        tabIndex={tabIndex}
        size={size}
        backgroundColor={backgroundColor}
      >
        {children}
      </StyledDrawer>
    </>
  );
};

type StyledDrawerProps = Pick<DrawerProps, "isOpen" | "size"> & {
  hasTransitionedIn: boolean;
  backgroundColor?: StyleObject["backgroundColor"];
};

const StyledDrawer = styled(StyledPopoverBase)<StyledDrawerProps>((
  { isOpen, size, hasTransitionedIn, backgroundColor },
) => {
  const initialTransform = size === "full"
    ? "translateY($spacing-48)"
    : "translateY(100%)";
  const targetTransform = "translateY(0)";
  const initialOpacity = size === "full" ? 0 : 1;
  const targetOpacity = 1;
  const transformTransitionDuration = size === "full" ? "300ms" : "155ms";

  return {
    position: "fixed",
    backgroundColor: backgroundColor ?? "$background-tertiary",
    top: "auto",
    left: 0,
    right: 0,
    bottom: 0,
    height: size === "full" ? "calc(100% - $spacing-32)" : "auto",
    maxHeight: "100%",
    transform: isOpen && hasTransitionedIn ? targetTransform : initialTransform,
    opacity: isOpen && hasTransitionedIn ? targetOpacity : initialOpacity,
    transition: `
      transform ${transformTransitionDuration} ease,
      opacity 100ms linear
    `,
    padding: ["$spacing-16", "$spacing-8", "$spacing-8", "$spacing-8"],
    borderRadius: ["$radius-medium", "$radius-medium", 0, 0],
    overflowX: "hidden",
    overflowY: "auto",
    ...getFocusStyle({
      outlineOffset: "-2px",
    }),
    "@media (prefers-reduced-motion: reduce)": {
      transitionDuration: "0ms",
    },
  };
});
